import { AD_MANAGER_MEDIA_UPLOAD_MAX_FILES } from '@/config/constants';
import {
  MediaValidationIssueCode,
  MediaValidationResult
} from '@/utils/media/validateMediaConstraints';

import {
  supportedImageMimeTypes,
  supportedMediaContentTypeByExtension,
  supportedVideoMimeTypes
} from './constants';
import { SelectedMediaFile, TranslateFn, UploadableMediaKind } from './types';

export type AdManagerMediaLibraryAsset = {
  uuid?: string | null;
  name?: string | null;
  file_size?: number | null;
  is_image?: boolean | null;
  is_video?: boolean | null;
  is_pdf?: boolean | null;
  preview_image?: string | null;
  thumbnail_image?: string | null;
  original_asset?: string | null;
};

export type AdManagerMediaLibrarySelectionResult = {
  files: SelectedMediaFile[];
  hasMixedMedia: boolean;
  rejectedUnsupportedCount: number;
  skippedDuplicateCount: number;
  reachedLimit: boolean;
};

export const isUploadableMediaKind = (
  kind?: SelectedMediaFile['kind'] | null
): kind is UploadableMediaKind => kind === 'image' || kind === 'video';

export const getFileExtension = (fileName = '') =>
  fileName.split('.').pop()?.toLowerCase() || '';

export const getSupportedMediaContentType = (file: File) => {
  const mimeType = file.type.toLowerCase();

  if (
    supportedImageMimeTypes.has(mimeType) ||
    supportedVideoMimeTypes.has(mimeType)
  ) {
    return mimeType;
  }

  return (
    supportedMediaContentTypeByExtension[getFileExtension(file.name)] || null
  );
};

export const getSupportedMediaKind = (
  file: File
): UploadableMediaKind | null => {
  const contentType = getSupportedMediaContentType(file);

  if (!contentType) return null;
  if (supportedImageMimeTypes.has(contentType)) return 'image';
  if (supportedVideoMimeTypes.has(contentType)) return 'video';

  return null;
};

export const getMediaKind = (file: File): SelectedMediaFile['kind'] => {
  return getSupportedMediaKind(file) || 'file';
};

export const getSelectedMediaKind = (
  selectedMediaFiles: Array<Pick<SelectedMediaFile, 'kind'>>
): UploadableMediaKind | null => {
  const selectedKind = selectedMediaFiles.find((file) =>
    isUploadableMediaKind(file.kind)
  )?.kind;

  return isUploadableMediaKind(selectedKind) ? selectedKind : null;
};

export const formatFileSize = (size: number) => {
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const parseMediaLibrarySelectionPayload = (
  payload: unknown
): AdManagerMediaLibraryAsset[] => {
  if (!payload) return [];

  try {
    const parsedPayload =
      typeof payload === 'string' ? JSON.parse(payload) : payload;

    return Array.isArray(parsedPayload) ? parsedPayload : [parsedPayload];
  } catch (_error) {
    return [];
  }
};

export const getMediaLibraryAssetKind = (
  asset: AdManagerMediaLibraryAsset
): UploadableMediaKind | null => {
  if (asset?.is_video) return 'video';
  if (asset?.is_image) return 'image';

  return null;
};

export const normalizeMediaLibraryAsset = (
  asset: AdManagerMediaLibraryAsset
): SelectedMediaFile | null => {
  const uuid = String(asset?.uuid || '').trim();
  const kind = getMediaLibraryAssetKind(asset);

  if (!uuid || !kind) return null;

  const fileSize = Number(asset.file_size);
  const previewUrl =
    asset.preview_image || asset.thumbnail_image || asset.original_asset || '';

  return {
    id: uuid,
    imageUuid: uuid,
    kind,
    meta:
      Number.isFinite(fileSize) && fileSize > 0
        ? formatFileSize(fileSize)
        : undefined,
    name: String(asset.name || '').trim() || 'Untitled media',
    previewUrl: previewUrl || undefined
  };
};

export const getMediaLibrarySelectionResult = (
  assets: AdManagerMediaLibraryAsset[],
  selectedMediaFiles: SelectedMediaFile[] = [],
  maxFiles = AD_MANAGER_MEDIA_UPLOAD_MAX_FILES
): AdManagerMediaLibrarySelectionResult => {
  const normalizedFiles: SelectedMediaFile[] = [];
  let rejectedUnsupportedCount = 0;

  assets.forEach((asset) => {
    const normalizedFile = normalizeMediaLibraryAsset(asset);
    if (normalizedFile) {
      normalizedFiles.push(normalizedFile);
    } else {
      rejectedUnsupportedCount += 1;
    }
  });

  const selectedIds = new Set(
    selectedMediaFiles.map((file) => file.imageUuid || String(file.id))
  );
  const candidateIds = new Set<string>();
  let skippedDuplicateCount = 0;
  const uniqueFiles = normalizedFiles.filter((file) => {
    const fileId = file.imageUuid || String(file.id);
    if (selectedIds.has(fileId) || candidateIds.has(fileId)) {
      skippedDuplicateCount += 1;
      return false;
    }

    candidateIds.add(fileId);
    return true;
  });
  const uniqueKinds = new Set(
    uniqueFiles.map((file) => file.kind).filter(isUploadableMediaKind)
  );
  const selectedKind = getSelectedMediaKind(selectedMediaFiles);
  const hasMixedMedia =
    uniqueKinds.size > 1 ||
    Boolean(
      selectedKind && uniqueKinds.size > 0 && !uniqueKinds.has(selectedKind)
    );
  const availableSlots = Math.max(0, maxFiles - selectedMediaFiles.length);

  if (hasMixedMedia) {
    return {
      files: [],
      hasMixedMedia,
      rejectedUnsupportedCount,
      skippedDuplicateCount,
      reachedLimit: availableSlots === 0 || uniqueFiles.length > availableSlots
    };
  }

  return {
    files: uniqueFiles.slice(0, availableSlots),
    hasMixedMedia,
    rejectedUnsupportedCount,
    skippedDuplicateCount,
    reachedLimit: uniqueFiles.length > availableSlots
  };
};

export const getUploadableMediaFiles = (
  mediaFiles: File[],
  selectedMediaFiles: Array<Pick<SelectedMediaFile, 'kind'>> = []
) => {
  const availableSlots = Math.max(
    0,
    AD_MANAGER_MEDIA_UPLOAD_MAX_FILES - selectedMediaFiles.length
  );
  const uploadableMediaFiles = mediaFiles.filter((file) =>
    getSupportedMediaKind(file)
  );
  const uploadKinds = new Set(
    uploadableMediaFiles
      .map(getSupportedMediaKind)
      .filter(isUploadableMediaKind)
  );
  const selectedMediaKind = getSelectedMediaKind(selectedMediaFiles);

  if (
    uploadKinds.size > 1 ||
    (selectedMediaKind &&
      uploadKinds.size > 0 &&
      !uploadKinds.has(selectedMediaKind))
  ) {
    return [];
  }

  return uploadableMediaFiles.slice(0, availableSlots);
};

export const getUnsupportedMediaFiles = (mediaFiles: File[]) =>
  mediaFiles.filter((file) => !getSupportedMediaKind(file));

export const hasMixedMediaUpload = (
  mediaFiles: File[],
  selectedMediaFiles: Array<Pick<SelectedMediaFile, 'kind'>> = []
) => {
  const mediaKinds = new Set<UploadableMediaKind>();

  selectedMediaFiles.forEach((file) => {
    if (isUploadableMediaKind(file.kind)) {
      mediaKinds.add(file.kind);
    }
  });

  mediaFiles.forEach((file) => {
    const mediaKind = getSupportedMediaKind(file);
    if (mediaKind) {
      mediaKinds.add(mediaKind);
    }
  });

  return mediaKinds.size > 1;
};

export const formatFileNameList = (
  files: File[],
  t: TranslateFn = (_key, fallback) => fallback
) => {
  const visibleFileNames = files
    .slice(0, 3)
    .map(
      (file) =>
        file.name || t('adManager.media.untitledFile', 'Untitled file')
    );
  const hiddenFileCount = files.length - visibleFileNames.length;

  return hiddenFileCount > 0
    ? `${visibleFileNames.join(', ')} +${hiddenFileCount} ${t(
        'adManager.media.more',
        'more'
      )}`
    : visibleFileNames.join(', ');
};

export const getUploadPresetMediaUuid = (uploadPreset: any) =>
  uploadPreset?.uploaded_object_data?.uuid || uploadPreset?.uuid || null;

const ASPECT_RATIO_MIXED_HINT =
  ' Because you selected placements from multiple categories, the stricter feed range applies. Narrow the placement selection or adjust the media to fit.';

export const MEDIA_VALIDATION_FALLBACKS: Record<string, string> = {
  imageSizeExceeded: 'Image exceeds the 30 MB size limit.',
  videoSizeExceeded: 'Video exceeds the 1 GB size limit.',
  resolutionTooLow:
    'Resolution too low. The shorter side must be at least 600 px.',
  resolutionBelowRecommended:
    'Resolution below recommendation. 1080 px on the shorter side is recommended.',
  aspectRatioOutOfRange:
    'Aspect ratio {ratio}:1 is not compatible with the selected placements. Allowed: {min}:1 to {max}:1.',
  aspectRatioOutOfRangeMixed:
    'Aspect ratio {ratio}:1 is not compatible with the selected placements. Allowed: {min}:1 to {max}:1.' +
    ASPECT_RATIO_MIXED_HINT
};

const formatRatioValue = (value: number) =>
  Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(/\.?0+$/, '');

export const formatMediaValidationDetail = (
  code: MediaValidationIssueCode,
  detail: Record<string, number> | undefined
): Record<string, string | number> | undefined => {
  if (!detail) return detail;
  if (code !== 'aspectRatioOutOfRange') return detail;
  return {
    ...detail,
    ratio: formatRatioValue(detail.ratio),
    min: formatRatioValue(detail.min),
    max: formatRatioValue(detail.max)
  };
};

export type MediaValidationMessageDescriptor = {
  key: string;
  fallback: string;
  detail: Record<string, string | number> | undefined;
};

export const getMediaValidationMessageDescriptor = (
  code: MediaValidationIssueCode,
  detail: Record<string, number> | undefined,
  placementCategoryCount: number
): MediaValidationMessageDescriptor => {
  const useMixedVariant =
    code === 'aspectRatioOutOfRange' && placementCategoryCount > 1;
  const key = useMixedVariant
    ? 'aspectRatioOutOfRangeMixed'
    : (code as string);
  return {
    key: `adManager.mediaValidation.${key}`,
    fallback: MEDIA_VALIDATION_FALLBACKS[key],
    detail: formatMediaValidationDetail(code, detail)
  };
};

export type MediaValidationNoticeLevel = 'error' | 'warning';

export type MediaValidationNoticeItem = {
  fileName: string;
  message: string;
};

export type MediaValidationNotice = {
  level: MediaValidationNoticeLevel;
  items: MediaValidationNoticeItem[];
};

export type MediaValidationEntry = {
  fileName: string;
  result: MediaValidationResult;
};

export const buildMediaValidationNotice = (
  entries: MediaValidationEntry[],
  placementCategoryCount: number,
  translate: (descriptor: MediaValidationMessageDescriptor) => string
): MediaValidationNotice | null => {
  const collect = (
    pick: (result: MediaValidationResult) => Array<{
      code: MediaValidationIssueCode;
      detail?: Record<string, number>;
    }>
  ): MediaValidationNoticeItem[] =>
    entries.flatMap((entry) =>
      pick(entry.result).map((issue) => ({
        fileName: entry.fileName,
        message: translate(
          getMediaValidationMessageDescriptor(
            issue.code,
            issue.detail,
            placementCategoryCount
          )
        )
      }))
    );

  const errors = collect((result) => result.errors);
  if (errors.length > 0) return { level: 'error', items: errors };

  const warnings = collect((result) => result.warnings);
  if (warnings.length > 0) return { level: 'warning', items: warnings };

  return null;
};
